import React, { useMemo } from 'react';
import BooleanRadio from '../../../components/common/BooleanRadio/BooleanRadio';
import radioStyles from '../../../components/common/BooleanRadio/BooleanRadio.module.css';
import { AppFormInput, AppFormSearchableSelect, AppFormTextarea, AppInputLabel } from '../../../components/common/appForms';
import { AppSectionTitle } from '../../../components/common/appUi';
import Text from '../../../components/common/Text/Text';
import { CALLING_CODES, CODE_TO_ISO } from '../../../constants/callingCodes';
import {
  phoneMaxLength,
  phonePlaceholder,
  postalCodeMaxLength,
  postalCodePlaceholder,
  formatHeightMeters,
  formatWeightKg,
} from '../../../utils/masks';
import AnamneseObservation from './AnamneseObservation';
import {
  PUBLIC_AVAILABLE_TRAINING_TIME_OPTIONS,
  PUBLIC_MAIN_GOAL_OPTIONS,
  PUBLIC_ONLINE_COURSE_OPTIONS,
  PUBLIC_REFERRAL_SOURCE_OPTIONS,
  PUBLIC_TRAINING_LEVEL_OPTIONS,
  PUBLIC_TRAINING_LOCATION_OPTIONS,
  PUBLIC_WEEKLY_AVAILABILITY_OPTIONS,
  PUBLIC_ANAMNESE_FIELD_LABELS,
  withCurrentSelectOption,
} from './publicAnamnese.constants';
import type { PublicAnamneseFormState, PublicAnamneseLockedFields } from './publicAnamnese.types';
import { createEmptyPublicAnamneseLockedFields } from './publicAnamnese.utils';
import formStyles from './PublicAnamneseForm.module.css';
import styles from '../../Fup/Fup.module.css';

interface PublicAnamneseFormProps {
  data: PublicAnamneseFormState;
  errors: Record<string, string>;
  disabled?: boolean;
  lockedFields?: PublicAnamneseLockedFields;
  loadingCep?: boolean;
  minimalPersonalData?: boolean;
  onFieldChange: <K extends keyof PublicAnamneseFormState>(
    field: K,
    value: PublicAnamneseFormState[K],
  ) => void;
  onCepBlur: () => void;
}

interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

interface BooleanFieldProps {
  label: string;
  name: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

const FormSection: React.FC<FormSectionProps> = ({ title, description, children }) => (
  <section className={formStyles.formSection}>
    <AppSectionTitle className={formStyles.sectionTitle}>{title}</AppSectionTitle>
    {description && <p className={styles.description}>{description}</p>}
    <div className={formStyles.fieldStack}>{children}</div>
  </section>
);

const toFieldElementId = (name: string): string => (
  `public-anamnese-${name.replace(/([A-Z])/g, '-$1').toLowerCase()}`
);

const BooleanField: React.FC<BooleanFieldProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  disabled = false,
  required = true,
  className = '',
}) => (
  <div
    id={toFieldElementId(name)}
    className={[formStyles.feedbackField, className].filter(Boolean).join(' ')}
  >
    <AppInputLabel compact required={required}>{label}</AppInputLabel>
    <BooleanRadio
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      error={error}
    />
  </div>
);

const PublicAnamneseForm: React.FC<PublicAnamneseFormProps> = ({
  data,
  errors,
  disabled = false,
  lockedFields = createEmptyPublicAnamneseLockedFields(),
  loadingCep = false,
  minimalPersonalData = false,
  onFieldChange,
  onCepBlur,
}) => {
  const labels = PUBLIC_ANAMNESE_FIELD_LABELS;
  const callingCodeOptions = useMemo(
    () => [...CALLING_CODES]
      .sort((a, b) => a.label.localeCompare(b.label, 'pt'))
      .map((entry) => ({ value: entry.value, label: entry.label })),
    [],
  );

  const countryForPostal = data.country || CODE_TO_ISO[data.callingCode] || 'BR';

  return (
    <div className={formStyles.formWrapper}>
      <FormSection title="Dados Pessoais">

        <div className={formStyles.nameEmailFields}>
          <AppFormInput
            id="public-anamnese-student-name"
            name="studentName"
            label={labels.studentName}
            value={data.studentName}
            onChange={(event) => onFieldChange('studentName', event.target.value)}
            placeholder="Digite seu nome completo"
            error={errors.studentName}
            disabled={disabled}
            readOnly={lockedFields.studentName}
            required
          />

          <AppFormInput
            id="public-anamnese-email"
            name="email"
            label={labels.email}
            type="email"
            value={data.email}
            onChange={(event) => onFieldChange('email', event.target.value)}
            placeholder="email@exemplo.com"
            error={errors.email}
            disabled={disabled}
            readOnly={lockedFields.email}
            required
          />
        </div>

        <div className={formStyles.nameEmailFields}>
          <AppFormInput
            id="public-anamnese-height"
            name="heightCm"
            label={labels.heightCm}
            value={data.heightCm}
            onChange={(event) => onFieldChange('heightCm', event.target.value)}
            onBlur={() => onFieldChange('heightCm', formatHeightMeters(data.heightCm))}
            placeholder="1,75"
            inputMode="decimal"
            maxLength={4}
            error={errors.heightCm}
            disabled={disabled}
            required
          />

          <AppFormInput
            id="public-anamnese-weight"
            name="weightKg"
            label={labels.weightKg}
            value={data.weightKg}
            onChange={(event) => onFieldChange('weightKg', event.target.value)}
            onBlur={() => onFieldChange('weightKg', formatWeightKg(data.weightKg))}
            placeholder="60,45"
            inputMode="decimal"
            maxLength={6}
            error={errors.weightKg}
            disabled={disabled}
            required
          />
        </div>


        <div className={minimalPersonalData ? formStyles.hiddenField : undefined}>
        <AppFormInput
          id="public-anamnese-birth-date"
          name="birthDate"
          label={labels.birthDate}
          type="date"
          value={data.birthDate}
          onChange={(event) => onFieldChange('birthDate', event.target.value)}
          error={errors.birthDate}
          disabled={disabled}
          readOnly={lockedFields.birthDate}
          required
        />

        <AppFormInput
          id="public-anamnese-cpf"
          name="cpf"
          label={labels.cpf}
          value={data.cpf}
          onChange={(event) => onFieldChange('cpf', event.target.value)}
          placeholder="Digite seu CPF"
          inputMode="numeric"
          error={errors.cpf}
          disabled={disabled}
          readOnly={lockedFields.cpf}
          required
        />

        <div className={formStyles.feedbackField}>
          <AppInputLabel required={!lockedFields.phone}>{labels.phone}</AppInputLabel>
          <div className={styles.formGrid}>
            <AppFormSearchableSelect
              id="public-anamnese-calling-code"
              name="callingCode"
              label="DDI"
              value={data.callingCode}
              onChange={(event) => onFieldChange('callingCode', event.target.value)}
              options={callingCodeOptions}
              searchPlaceholder="Pesquisar"
              error={errors.callingCode}
              disabled={disabled}
              readOnly={lockedFields.phone}
              required
              allowEmpty={false}
            />

            <AppFormInput
              id="public-anamnese-phone"
              name="phone"
              label="Número com DDD"
              value={data.phone}
              onChange={(event) => onFieldChange('phone', event.target.value)}
              placeholder={phonePlaceholder(data.callingCode || '55')}
              maxLength={phoneMaxLength(data.callingCode || '55')}
              inputMode="tel"
              error={errors.phone}
              disabled={disabled}
              readOnly={lockedFields.phone}
              required
            />
          </div>
        </div>
        </div>

        <div className={formStyles.hiddenField}>
          <AppFormInput
            id="public-anamnese-cep"
            name="cep"
            label="CEP"
            value={data.cep}
            onChange={(event) => onFieldChange('cep', event.target.value)}
            onBlur={onCepBlur}
            placeholder={postalCodePlaceholder(countryForPostal)}
            maxLength={postalCodeMaxLength(countryForPostal)}
            error={errors.cep}
            disabled={disabled || loadingCep}
          />

          <AppFormInput
            id="public-anamnese-street"
            name="street"
            label="Rua"
            value={data.street}
            onChange={(event) => onFieldChange('street', event.target.value)}
            placeholder="Nome da rua"
            error={errors.street}
            disabled={disabled}
          />

          <AppFormInput
            id="public-anamnese-number"
            name="number"
            label="Número"
            value={data.number}
            onChange={(event) => onFieldChange('number', event.target.value)}
            placeholder="123"
            error={errors.number}
            disabled={disabled}
          />

          <AppFormInput
            id="public-anamnese-complement"
            name="complement"
            label="Complemento"
            value={data.complement}
            onChange={(event) => onFieldChange('complement', event.target.value)}
            placeholder="Apto, bloco..."
            error={errors.complement}
            disabled={disabled}
          />

          <AppFormInput
            id="public-anamnese-neighborhood"
            name="neighborhood"
            label="Bairro"
            value={data.neighborhood}
            onChange={(event) => onFieldChange('neighborhood', event.target.value)}
            placeholder="Bairro"
            error={errors.neighborhood}
            disabled={disabled}
          />

          <AppFormInput
            id="public-anamnese-city"
            name="city"
            label="Cidade"
            value={data.city}
            onChange={(event) => onFieldChange('city', event.target.value)}
            placeholder="Cidade"
            error={errors.city}
            disabled={disabled}
          />

          <AppFormInput
            id="public-anamnese-state"
            name="state"
            label="Estado"
            value={data.state}
            onChange={(event) => onFieldChange('state', event.target.value)}
            placeholder="UF ou estado"
            error={errors.state}
            disabled={disabled}
          />

          <AppFormInput
            id="public-anamnese-country"
            name="country"
            label="País"
            value={data.country}
            onChange={(event) => onFieldChange('country', event.target.value)}
            placeholder="BR"
            error={errors.country}
            disabled={disabled}
          />
        </div>

        <AppFormInput
          id="public-anamnese-profession"
          name="profession"
          label={labels.profession}
          value={data.profession}
          onChange={(event) => onFieldChange('profession', event.target.value)}
          placeholder="Digite sua profissão"
          error={errors.profession}
          disabled={disabled}
        />

        <AppFormInput
          id="public-anamnese-instagram"
          name="instagram"
          label={labels.instagram}
          value={data.instagram}
          onChange={(event) => onFieldChange('instagram', event.target.value)}
          placeholder="@usuario"
          error={errors.instagram}
          disabled={disabled}
        />
      </FormSection>

      <FormSection title="Relacionamento com a Consultoria">
        <div className={formStyles.feedbackField}>
          <AppFormTextarea
            id="public-anamnese-previous-consultancy"
            name="previousOnlineConsultancy"
            label={labels.previousOnlineConsultancy}
            value={data.previousOnlineConsultancy}
            onChange={(event) => onFieldChange('previousOnlineConsultancy', event.target.value)}
            placeholder="Conte se já fez consultoria online e, se sim, por que interrompeu"
            error={errors.previousOnlineConsultancy}
            disabled={disabled}
            required
          />
        </div>

        <AppFormSearchableSelect
          id="public-anamnese-online-course"
          name="onlineCourse"
          label={labels.onlineCourse}
          value={data.onlineCourse}
          onChange={(event) => onFieldChange('onlineCourse', event.target.value as PublicAnamneseFormState['onlineCourse'])}
          options={PUBLIC_ONLINE_COURSE_OPTIONS}
          searchPlaceholder="Pesquisar"
          error={errors.onlineCourse}
          disabled={disabled}
          required
          allowEmpty={false}
        />

        {data.onlineCourse === 'other' && (
          <AppFormInput
            id="public-anamnese-other-course"
            name="otherCourse"
            label="Qual curso?"
            value={data.otherCourse}
            onChange={(event) => onFieldChange('otherCourse', event.target.value)}
            placeholder="Informe qual curso"
            error={errors.otherCourse}
            disabled={disabled}
            required
          />
        )}

        <div className={formStyles.feedbackField}>
          <AppFormTextarea
            id="public-anamnese-course-feedback"
            name="courseFeedback"
            label={labels.courseFeedback}
            value={data.courseFeedback}
            onChange={(event) => onFieldChange('courseFeedback', event.target.value)}
            placeholder="Compartilhe o feedback sobre os cursos e sua fase atual"
            error={errors.courseFeedback}
            disabled={disabled}
            required
          />
        </div>
      </FormSection>

      <FormSection title="Experiência de Treinamento">
        <div id="public-anamnese-training-level" className={`${formStyles.feedbackField} ${formStyles.trainingLevelField}`}>
          <AppInputLabel compact required>{labels.trainingLevel}</AppInputLabel>
          {PUBLIC_TRAINING_LEVEL_OPTIONS.map((option) => {
            const optionId = `public-anamnese-training-level-${option.value}`;

            return (
              <div key={option.value}>
                <label htmlFor={optionId} className={radioStyles.radioLabel}>
                  <input
                    id={optionId}
                    type="radio"
                    name="trainingLevel"
                    value={option.value}
                    checked={data.trainingLevel === option.value}
                    onChange={(event) => onFieldChange(
                      'trainingLevel',
                      event.target.value as PublicAnamneseFormState['trainingLevel'],
                    )}
                    disabled={disabled}
                  />
                  <strong>{option.label}</strong>
                </label>
                {option.description && (
                  <Text
                    variant="body"
                    as="p"
                    className={formStyles.trainingLevelDescription}
                  >
                    {option.description}
                  </Text>
                )}
              </div>
            );
          })}
          {errors.trainingLevel && (
            <span className={radioStyles.errorText}>{errors.trainingLevel}</span>
          )}
        </div>

        {data.trainingLevel === 'other' && (
          <AppFormInput
            id="public-anamnese-training-level-other"
            name="trainingLevelOther"
            label="Descreva o nível de treinamento"
            value={data.trainingLevelOther}
            onChange={(event) => onFieldChange('trainingLevelOther', event.target.value)}
            placeholder="Descreva seu nível"
            error={errors.trainingLevelOther}
            disabled={disabled}
            required
          />
        )}

        <AnamneseObservation observationKey="trainingLevel" />

        <AppFormSearchableSelect
          id="public-anamnese-training-location"
          name="trainingLocation"
          label={labels.trainingLocation}
          value={data.trainingLocation}
          onChange={(event) => onFieldChange('trainingLocation', event.target.value as PublicAnamneseFormState['trainingLocation'])}
          options={PUBLIC_TRAINING_LOCATION_OPTIONS}
          searchPlaceholder="Pesquisar"
          error={errors.trainingLocation}
          disabled={disabled}
          required
          allowEmpty={false}
        />

        <AnamneseObservation observationKey="trainingLocation" />

        {data.trainingLocation === 'full_gym' && (
          <div className={formStyles.feedbackField}>
            <AppFormTextarea
              id="public-anamnese-full-gym-name"
              name="fullGymName"
              label={labels.fullGymName}
              value={data.fullGymName}
              onChange={(event) => onFieldChange('fullGymName', event.target.value)}
              placeholder="Informe o nome da academia e descreva os equipamentos disponíveis"
              error={errors.fullGymName}
              disabled={disabled}
              required
            />
          </div>
        )}

        <AnamneseObservation
          observationKey="homeTrainingMaterials"
          visible={data.trainingLocation === 'home_condo_gym'}
        />

        {data.trainingLocation === 'home_condo_gym' && (
          <div className={formStyles.feedbackField}>
            <AppFormTextarea
              id="public-anamnese-available-equipment"
              name="availableEquipment"
              label={labels.availableEquipment}
              value={data.availableEquipment}
              onChange={(event) => onFieldChange('availableEquipment', event.target.value)}
              placeholder="Descreva os materiais disponíveis para treino em casa"
              error={errors.availableEquipment}
              disabled={disabled}
              required
            />
          </div>
        )}

        <AppFormSearchableSelect
          id="public-anamnese-weekly-availability"
          name="weeklyAvailability"
          label={labels.weeklyAvailability}
          value={data.weeklyAvailability}
          onChange={(event) => onFieldChange('weeklyAvailability', event.target.value)}
          options={withCurrentSelectOption(PUBLIC_WEEKLY_AVAILABILITY_OPTIONS, data.weeklyAvailability)}
          searchPlaceholder="Pesquisar"
          error={errors.weeklyAvailability}
          disabled={disabled}
          required
          allowEmpty={false}
        />

        <AppFormSearchableSelect
          id="public-anamnese-available-training-time"
          name="availableTrainingTime"
          label={labels.availableTrainingTime}
          value={data.availableTrainingTime}
          onChange={(event) => onFieldChange('availableTrainingTime', event.target.value)}
          options={withCurrentSelectOption(PUBLIC_AVAILABLE_TRAINING_TIME_OPTIONS, data.availableTrainingTime)}
          searchPlaceholder="Pesquisar"
          error={errors.availableTrainingTime}
          disabled={disabled}
          required
          allowEmpty={false}
        />

        <AnamneseObservation observationKey="weeklyAvailability" />
      </FormSection>

      <FormSection title="Saúde e Lesões">
        <BooleanField
          label={labels.hadInjury}
          name="hadInjury"
          value={data.hadInjury}
          onChange={(value) => onFieldChange('hadInjury', value)}
          error={errors.hadInjury}
          disabled={disabled}
        />

        {data.hadInjury === true && (
          <div className={formStyles.feedbackField}>
            <AppFormTextarea
              id="public-anamnese-injury-description"
              name="injuryDescription"
              label={labels.injuryDescription}
              value={data.injuryDescription}
              onChange={(event) => onFieldChange('injuryDescription', event.target.value)}
              placeholder="Descreva a lesão"
              error={errors.injuryDescription}
              disabled={disabled}
              required
            />
          </div>
        )}

        <BooleanField
          label={labels.hasPain}
          name="hasPain"
          value={data.hasPain}
          onChange={(value) => onFieldChange('hasPain', value)}
          error={errors.hasPain}
          disabled={disabled}
        />

        {data.hasPain === true && (
          <>
            <AppFormInput
              id="public-anamnese-pain-location"
              name="painLocation"
              label={labels.painLocation}
              value={data.painLocation}
              onChange={(event) => onFieldChange('painLocation', event.target.value)}
              placeholder="Onde sente dor?"
              error={errors.painLocation}
              disabled={disabled}
              required
            />

            <BooleanField
              label={labels.painLimitsTraining}
              name="painLimitsTraining"
              value={data.painLimitsTraining}
              onChange={(value) => onFieldChange('painLimitsTraining', value)}
              error={errors.painLimitsTraining}
              disabled={disabled}
            />
          </>
        )}

        <AnamneseObservation
          observationKey="movementLimitations"
          visible={data.hasPain === true}
        />

        {data.hasPain === true && data.painLimitsTraining === true && (
          <div className={formStyles.feedbackField}>
            <AppFormTextarea
              id="public-anamnese-limited-exercises"
              name="limitedExercises"
              label={labels.limitedExercises}
              value={data.limitedExercises}
              onChange={(event) => onFieldChange('limitedExercises', event.target.value)}
              placeholder="Quais exercícios não consegue fazer?"
              error={errors.limitedExercises}
              disabled={disabled}
              required
            />
          </div>
        )}

        <AnamneseObservation observationKey="currentTraining" />
      </FormSection>

      <FormSection title="Atividade Física Atual">
        <BooleanField
          className={formStyles.firstSectionBooleanField}
          label={labels.currentlyExercises}
          name="currentlyExercises"
          value={data.currentlyExercises}
          onChange={(value) => onFieldChange('currentlyExercises', value)}
          error={errors.currentlyExercises}
          disabled={disabled}
        />

        {data.currentlyExercises === true && (
          <AppFormInput
            id="public-anamnese-exercise-modalities"
            name="exerciseModalities"
            label={labels.exerciseModalities}
            value={data.exerciseModalities}
            onChange={(event) => onFieldChange('exerciseModalities', event.target.value)}
            placeholder="Musculação, corrida..."
            error={errors.exerciseModalities}
            disabled={disabled}
            required
          />
        )}

        {data.currentlyExercises === false && (
          <AppFormInput
            id="public-anamnese-time-without-exercise"
            name="timeWithoutExercise"
            label={labels.timeWithoutExercise}
            value={data.timeWithoutExercise}
            onChange={(event) => onFieldChange('timeWithoutExercise', event.target.value)}
            placeholder="Ex: 6 meses"
            error={errors.timeWithoutExercise}
            disabled={disabled}
            required
          />
        )}

        <div className={formStyles.feedbackField}>
          <AppFormTextarea
            id="public-anamnese-exercise-history"
            name="exerciseHistory"
            label={labels.exerciseHistory}
            value={data.exerciseHistory}
            onChange={(event) => onFieldChange('exerciseHistory', event.target.value)}
            placeholder="Descreva seu histórico com exercícios"
            error={errors.exerciseHistory}
            disabled={disabled}
            required
          />
        </div>

        <BooleanField
          label={labels.enjoysTraining}
          name="enjoysTraining"
          value={data.enjoysTraining}
          onChange={(value) => onFieldChange('enjoysTraining', value)}
          error={errors.enjoysTraining}
          disabled={disabled}
        />

        <BooleanField
          label={labels.usedToEffort}
          name="usedToEffort"
          value={data.usedToEffort}
          onChange={(value) => onFieldChange('usedToEffort', value)}
          error={errors.usedToEffort}
          disabled={disabled}
        />
      </FormSection>

      <FormSection title="Nutrição e Estilo de Vida">
        <AnamneseObservation observationKey="nutritionalSupport" />

        <BooleanField
          label={labels.hasNutritionalSupport}
          name="hasNutritionalSupport"
          value={data.hasNutritionalSupport}
          onChange={(value) => onFieldChange('hasNutritionalSupport', value)}
          error={errors.hasNutritionalSupport}
          disabled={disabled}
        />

        <div className={formStyles.feedbackField}>
          <AppFormTextarea
            id="public-anamnese-diet-detail"
            name="dietDetail"
            label={labels.dietDetail}
            value={data.dietDetail}
            onChange={(event) => onFieldChange('dietDetail', event.target.value)}
            placeholder="Descreva alimentação, sono e saúde mental"
            error={errors.dietDetail}
            disabled={disabled}
            required
          />
        </div>
      </FormSection>

      <FormSection title="Objetivos">
        <AppFormSearchableSelect
          id="public-anamnese-main-goal"
          name="mainGoal"
          label={labels.mainGoal}
          value={data.mainGoal}
          onChange={(event) => onFieldChange('mainGoal', event.target.value as PublicAnamneseFormState['mainGoal'])}
          options={PUBLIC_MAIN_GOAL_OPTIONS}
          searchPlaceholder="Pesquisar"
          error={errors.mainGoal}
          disabled={disabled}
          required
          allowEmpty={false}
        />

        <div className={formStyles.feedbackField}>
          <AppFormTextarea
            id="public-anamnese-goal-details"
            name="goalDetails"
            label={labels.goalDetails}
            value={data.goalDetails}
            onChange={(event) => onFieldChange('goalDetails', event.target.value)}
            placeholder="Conte mais sobre seus objetivos"
            error={errors.goalDetails}
            disabled={disabled}
            required
          />
        </div>
      </FormSection>

      <FormSection title="Medicamentos e Histórico Médico">
        <BooleanField
          className={formStyles.firstSectionBooleanField}
          label={labels.takesMedication}
          name="takesMedication"
          value={data.takesMedication}
          onChange={(value) => onFieldChange('takesMedication', value)}
          error={errors.takesMedication}
          disabled={disabled}
        />

        {data.takesMedication === true && (
          <div className={formStyles.feedbackField}>
            <AppFormTextarea
              id="public-anamnese-medications"
              name="medications"
              label={labels.medications}
              value={data.medications}
              onChange={(event) => onFieldChange('medications', event.target.value)}
              placeholder="Informe qual medicamento"
              error={errors.medications}
              disabled={disabled}
              required
            />
          </div>
        )}

        <BooleanField
          label={labels.hadSurgery}
          name="hadSurgery"
          value={data.hadSurgery}
          onChange={(value) => onFieldChange('hadSurgery', value)}
          error={errors.hadSurgery}
          disabled={disabled}
        />

        {data.hadSurgery === true && (
          <div className={formStyles.feedbackField}>
            <AppFormTextarea
              id="public-anamnese-surgeries"
              name="surgeries"
              label={labels.surgeries}
              value={data.surgeries}
              onChange={(event) => onFieldChange('surgeries', event.target.value)}
              placeholder="Informe qual cirurgia"
              error={errors.surgeries}
              disabled={disabled}
              required
            />
          </div>
        )}

        <BooleanField
          label={labels.hasChronicDisease}
          name="hasChronicDisease"
          value={data.hasChronicDisease}
          onChange={(value) => onFieldChange('hasChronicDisease', value)}
          error={errors.hasChronicDisease}
          disabled={disabled}
        />

        {data.hasChronicDisease === true && (
          <div className={formStyles.feedbackField}>
            <AppFormTextarea
              id="public-anamnese-chronic-diseases"
              name="chronicDiseases"
              label={labels.chronicDiseases}
              value={data.chronicDiseases}
              onChange={(event) => onFieldChange('chronicDiseases', event.target.value)}
              placeholder="Informe qual doença"
              error={errors.chronicDiseases}
              disabled={disabled}
              required
            />
          </div>
        )}

        <BooleanField
          label={labels.hasFamilyHistory}
          name="hasFamilyHistory"
          value={data.hasFamilyHistory}
          onChange={(value) => onFieldChange('hasFamilyHistory', value)}
          error={errors.hasFamilyHistory}
          disabled={disabled}
        />

        {data.hasFamilyHistory === true && (
          <div className={formStyles.feedbackField}>
            <AppFormTextarea
              id="public-anamnese-family-history"
              name="familyHistoryDescription"
              label={labels.familyHistoryDescription}
              value={data.familyHistoryDescription}
              onChange={(event) => onFieldChange('familyHistoryDescription', event.target.value)}
              placeholder="Detalhe o histórico familiar"
              error={errors.familyHistoryDescription}
              disabled={disabled}
              required
            />
          </div>
        )}
      </FormSection>

      <FormSection title="Filhos e Pós-Parto">
        <BooleanField
          className={formStyles.firstSectionBooleanField}
          label={labels.hasChildren}
          name="hasChildren"
          value={data.hasChildren}
          onChange={(value) => onFieldChange('hasChildren', value)}
          error={errors.hasChildren}
          disabled={disabled}
        />

        <AnamneseObservation
          observationKey="postpartumDiastasis"
          visible={data.hasChildren === true}
        />

        {data.hasChildren === true && (
          <>
            <AppFormInput
              id="public-anamnese-time-since-delivery"
              name="timeSinceDelivery"
              label={labels.timeSinceDelivery}
              value={data.timeSinceDelivery}
              onChange={(event) => onFieldChange('timeSinceDelivery', event.target.value)}
              placeholder="Ex: 1 ano"
              error={errors.timeSinceDelivery}
              disabled={disabled}
              required
            />

            <BooleanField
              label={labels.abdominalDiastasis}
              name="abdominalDiastasis"
              value={data.abdominalDiastasis}
              onChange={(value) => onFieldChange('abdominalDiastasis', value)}
              error={errors.abdominalDiastasis}
              disabled={disabled}
            />

            {data.abdominalDiastasis === true && (
              <AppFormInput
                id="public-anamnese-diastasis-treatment"
                name="diastasisTreatment"
                label="Tratou a diástase?"
                value={data.diastasisTreatment}
                onChange={(event) => onFieldChange('diastasisTreatment', event.target.value)}
                placeholder="Informe se tratou a diástase"
                error={errors.diastasisTreatment}
                disabled={disabled}
                required
              />
            )}
          </>
        )}
      </FormSection>

      <FormSection title="Informações Adicionais">
        <div className={formStyles.feedbackField}>
          <AppFormTextarea
            id="public-anamnese-additional-info"
            name="additionalInfo"
            label={labels.additionalInfo}
            value={data.additionalInfo}
            onChange={(event) => onFieldChange('additionalInfo', event.target.value)}
            placeholder="Compartilhe qualquer informação adicional"
            error={errors.additionalInfo}
            disabled={disabled}
            required
          />
        </div>

        <AppFormSearchableSelect
          id="public-anamnese-referral-source"
          name="referralSource"
          label={labels.referralSource}
          value={data.referralSource}
          onChange={(event) => onFieldChange('referralSource', event.target.value as PublicAnamneseFormState['referralSource'])}
          options={PUBLIC_REFERRAL_SOURCE_OPTIONS}
          searchPlaceholder="Pesquisar"
          error={errors.referralSource}
          disabled={disabled}
          required
          allowEmpty={false}
        />

        <div className={formStyles.feedbackField}>
          <AppFormTextarea
            id="public-anamnese-motivation"
            name="motivation"
            label={labels.motivation}
            value={data.motivation}
            onChange={(event) => onFieldChange('motivation', event.target.value)}
            placeholder="Conte como chegou e o que motivou sua escolha"
            error={errors.motivation}
            disabled={disabled}
            required
          />
        </div>
      </FormSection>


      {/* 
      não vamos exibir isso aqui  
      <section className={formStyles.formSection}>
        <div className={styles.answeredBanner}>
          <p className={styles.answeredTitle}>Informações de Apoio</p>
          <p className={styles.answeredMeta}>
            Após enviar a anamnese, compartilhe também os materiais complementares listados abaixo com a equipe.
          </p>
          {SUPPORT_INFO_ITEMS.map((item) => (
            <p key={item} className={styles.answeredMeta}>
              <span className={styles.answeredMetaLabel}>•</span>
              {item}
            </p>
          ))}
        </div>
      </section> */}
    </div>
  );
};

export default PublicAnamneseForm;
